import React, { useEffect, useState } from "react";
import { admin } from "../../lib/api";

const stateColour = {
  GATED: "pill-slate",
  VERIFIED: "pill-navy",
  IN_PROGRESS: "pill-marigold",
  COMPLETED: "pill-seafoam",
  SCORED: "pill-seafoam",
  QUEUED: "pill-marigold",
  DELIVERED: "pill-seafoam",
  ENGAGED: "pill-seafoam",
  BOOKED: "pill-seafoam",
  ABANDONED: "pill-slate",
};

export default function DealBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all"); // all | with-report | delivered
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await admin.dealBoard();
        setRows(res.rows);
      } catch (e) {
        setErr("Could not load Deal Board.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = rows.filter((r) => {
    if (filter === "with-report" && !r.report_id) return false;
    if (filter === "delivered" && r.report_status !== "delivered") return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(needle) ||
      (r.organisation || "").toLowerCase().includes(needle) ||
      (r.work_email || "").toLowerCase().includes(needle) ||
      (r.country || "").toLowerCase().includes(needle) ||
      (r.sector || "").toLowerCase().includes(needle)
    );
  });

  const stats = {
    total: rows.length,
    withReport: rows.filter((r) => r.report_id).length,
    delivered: rows.filter((r) => r.report_status === "delivered").length,
    queued: rows.filter((r) => r.report_status === "queued").length,
  };

  return (
    <div data-testid="deal-board-page">
      <div className="cw-eyebrow">Deal Board</div>
      <h1 className="cw-display-l" style={{ marginTop: 8 }}>All respondents.</h1>
      <p className="cw-body" style={{ marginTop: 8, color: "rgba(22,30,27,0.72)" }}>
        Every gate submission and every completed audit. Click <em>Download PDF</em> on any row that
        has a report.
      </p>

      <div className="cw-metric-grid" style={{ marginTop: 32 }}>
        <div className="cw-metric" data-testid="dealboard-metric-total">
          <div className="cw-label">Respondents captured</div>
          <div className="val">{stats.total}</div>
        </div>
        <div className="cw-metric" data-testid="dealboard-metric-with-report">
          <div className="cw-label">Reports generated</div>
          <div className="val">{stats.withReport}</div>
        </div>
        <div className="cw-metric" data-testid="dealboard-metric-delivered">
          <div className="cw-label">Reports delivered</div>
          <div className="val">{stats.delivered}</div>
        </div>
        <div className="cw-metric" data-testid="dealboard-metric-queued">
          <div className="cw-label">Awaiting release</div>
          <div className="val">{stats.queued}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <input
          type="text"
          className="cw-input"
          placeholder="Search by name, organisation, email, country, sector"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 380, flex: 1 }}
          data-testid="dealboard-search"
        />
        <div style={{ display: "flex", gap: 8 }}>
          {[
            ["all", "All respondents"],
            ["with-report", "With report"],
            ["delivered", "Delivered"],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`cw-btn ${filter === id ? "light-primary" : "cw-btn-text"}`}
              style={filter === id ? { height: 40, padding: "0 16px" } : {}}
              onClick={() => setFilter(id)}
              data-testid={`dealboard-filter-${id}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{ marginTop: 24 }} data-testid="deal-board-loading">Loading...</p>}
      {err && <p style={{ marginTop: 24, color: "var(--cw-error)" }}>{err}</p>}

      {!loading && !err && (
        <div className="cw-card" style={{ padding: 0, overflowX: "auto" }}>
          <table className="cw-table" data-testid="deal-board-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Name</th>
                <th>Organisation</th>
                <th>Country</th>
                <th>Sector</th>
                <th>State</th>
                <th>Tier</th>
                <th>Band</th>
                <th>Score</th>
                <th>Report</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`deal-row-${r.id}`}>
                  <td>{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.full_name}</div>
                    <div className="cw-small" style={{ marginTop: 2, color: "rgba(22,30,27,0.68)" }}>{r.work_email}</div>
                  </td>
                  <td>{r.organisation}</td>
                  <td>{r.country}</td>
                  <td>{r.sector}</td>
                  <td>
                    <span className={`pill ${stateColour[r.state] || "pill-slate"}`}>{r.state}</span>
                  </td>
                  <td>{r.lead_tier || "-"}</td>
                  <td>{r.readiness_band || "-"}</td>
                  <td>{r.lead_score ?? "-"}</td>
                  <td>
                    {r.report_id ? (
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <a
                          className="cw-btn-text"
                          href={admin.pdfUrl(r.report_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`download-pdf-${r.id}`}
                        >Download PDF</a>
                        <span className={`pill ${r.report_status === "delivered" ? "pill-seafoam" : r.report_status === "queued" ? "pill-marigold" : "pill-slate"}`}>
                          {r.report_status}
                        </span>
                      </div>
                    ) : (
                      <span className="cw-small" style={{ color: "rgba(22,30,27,0.60)" }}>
                        {r.state === "IN_PROGRESS" ? "In progress" : "Not yet"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", color: "rgba(22,30,27,0.68)" }}>No leads match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="cw-small" style={{ marginTop: 24, color: "rgba(22,30,27,0.68)" }}>
        A row appears here the moment a lead verifies their email. The report column links to the
        latest generated PDF for that respondent. TIER_1 respondents ({stats.withReport ? "" : "none yet"}) are
        held for review; every other tier auto-sends.
      </p>
    </div>
  );
}
